/**
 * Tâche 1424 (Eric 2026-10-01) — Qui peut créer des sondages.
 *
 * Eric a demandé des cases à cocher sur les NOMS DES PERSONNES, et non sur
 * les rôles : la matrice canaux × rôles juste au-dessus répond à une autre
 * question (qui peut LIRE quel canal), et mélanger les deux dans un même
 * tableau ferait cocher un droit en croyant en régler un autre.
 *
 * Les personnes qui éditent le contenu (admins, concepteurs, fondateurs) ont
 * une case cochée et VERROUILLÉE, avec la raison écrite : sans ce verrou, un
 * admin pourrait se décocher lui-même et perdre l'accès à l'écran qui permet
 * de se recocher.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { FloppyDisk, SpinnerGap, ChartBar } from "@phosphor-icons/react";

import { API_BASE as API } from "../../lib/apiBase";
import { useLang } from "../../contexts/LanguageContext";

export default function PollAccessPanel() {
  const { tr } = useLang();
  const [membres, setMembres] = useState([]);
  const [coches, setCoches] = useState(new Set());
  const [initial, setInitial] = useState(new Set());
  const [chargement, setChargement] = useState(true);
  const [enregistre, setEnregistre] = useState(false);

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      const { data } = await axios.get(`${API}/api/polls/access`, { withCredentials: true });
      const liste = data.members || [];
      setMembres(liste);
      const actifs = new Set(liste.filter((m) => m.can_create && !m.always_allowed).map((m) => m.id));
      setCoches(actifs);
      setInitial(new Set(actifs));
    } catch (err) {
      toast.error(err?.response?.data?.detail
        || tr("Impossible de charger la liste.", "Could not load the list."));
    } finally {
      setChargement(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { charger(); }, [charger]);

  const modifie = useMemo(() => {
    if (coches.size !== initial.size) return true;
    for (const id of coches) if (!initial.has(id)) return true;
    return false;
  }, [coches, initial]);

  const basculer = (id) => {
    setCoches((prec) => {
      const suivant = new Set(prec);
      if (suivant.has(id)) suivant.delete(id);
      else suivant.add(id);
      return suivant;
    });
  };

  const enregistrer = async () => {
    setEnregistre(true);
    try {
      await axios.put(`${API}/api/polls/access`,
        { user_ids: Array.from(coches) }, { withCredentials: true });
      toast.success(tr("Droits de sondage enregistrés", "Poll rights saved"));
      setInitial(new Set(coches));
    } catch (err) {
      toast.error(err?.response?.data?.detail
        || tr("Échec de l'enregistrement", "Save failed"));
    } finally {
      setEnregistre(false);
    }
  };

  if (chargement) {
    return (
      <div className="flex items-center justify-center gap-2 py-10 text-zinc-400">
        <SpinnerGap size={18} className="animate-spin" />
        {tr("Chargement…", "Loading…")}
      </div>
    );
  }

  return (
    <div data-testid="poll-access-panel">
      <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 font-bold text-zinc-900">
            <ChartBar size={18} weight="duotone" className="text-indigo-700" />
            {tr("Qui peut créer des sondages", "Who can create polls")}
          </h2>
          <p className="mt-1 max-w-xl text-xs text-zinc-500">
            {tr(
              "Cochez les personnes autorisées à créer un sondage, dans un canal comme dans une conversation. Les admins, concepteurs et fondateurs l'ont toujours.",
              "Check the people allowed to create a poll, in a channel as well as in a conversation. Admins, designers and founders always can.",
            )}
          </p>
        </div>
        <button
          type="button"
          onClick={enregistrer}
          disabled={!modifie || enregistre}
          className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-40"
          data-testid="poll-access-save-btn"
        >
          {enregistre ? <SpinnerGap size={15} className="animate-spin" /> : <FloppyDisk size={15} />}
          {tr("Enregistrer", "Save")}{modifie ? " *" : ""}
        </button>
      </div>

      <div className="overflow-hidden rounded-xl border border-zinc-200 bg-white">
        <table className="min-w-full text-sm" data-testid="poll-access-table">
          <thead>
            <tr className="border-b border-zinc-200 bg-zinc-50">
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">
                {tr("Personne", "Person")}
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">
                {tr("Rôle", "Role")}
              </th>
              <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wide text-zinc-500 whitespace-nowrap">
                {tr("Peut créer des sondages", "Can create polls")}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {membres.map((m) => {
              const coche = m.always_allowed || coches.has(m.id);
              return (
                <tr key={m.id} className="hover:bg-zinc-50/60" data-testid={`poll-access-row-${m.id}`}>
                  <td className="px-4 py-2.5">
                    <span className="font-medium text-zinc-800">{m.name}</span>
                    <span className="ml-2 text-[11px] text-zinc-400">{m.email}</span>
                  </td>
                  <td className="px-4 py-2.5 text-[11px] font-mono text-zinc-500">{m.role}</td>
                  <td className="px-4 py-2.5 text-center">
                    <input
                      type="checkbox"
                      checked={coche}
                      disabled={m.always_allowed}
                      onChange={() => basculer(m.id)}
                      className="h-4 w-4 cursor-pointer accent-indigo-600 disabled:cursor-not-allowed disabled:opacity-40"
                      data-testid={`poll-access-checkbox-${m.id}`}
                      title={m.always_allowed
                        ? tr("Ce rôle peut toujours créer des sondages.", "This role can always create polls.")
                        : undefined}
                    />
                    {m.always_allowed && (
                      <span className="ml-2 align-middle text-[10px] text-zinc-400">
                        {tr("toujours", "always")}
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
            {membres.length === 0 && (
              <tr>
                <td colSpan={3} className="px-4 py-10 text-center text-zinc-400">
                  {tr("Aucun membre.", "No member.")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
